import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Download, Loader2, RefreshCw, Video, Wand2 } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/_authenticated/create_/image/result/$id")({
  head: () => ({
    meta: [
      { title: "画像生成結果 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ImageResultPage,
});

type ImageGen = {
  id: string;
  status: string;
  output_image_url: string | null;
  prompt_original: string | null;
  prompt_translated: string | null;
  style_preset: string | null;
  aspect_ratio: string | null;
  quality_tier: string | null;
  credits_consumed: number;
  error_message: string | null;
};

function ImageResultPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [gen, setGen] = useState<ImageGen | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchOnce = async () => {
      const { data, error } = await supabase
        .from("generations")
        .select(
          "id, status, output_image_url, prompt_original, prompt_translated, style_preset, aspect_ratio, quality_tier, credits_consumed, error_message",
        )
        .eq("id", id)
        .maybeSingle();
      if (!active) return;
      if (error || !data) {
        setError(error?.message ?? "画像が見つかりません");
        setLoading(false);
        return;
      }
      setGen(data as ImageGen);
      setLoading(false);
    };
    fetchOnce();
    const t = window.setInterval(() => {
      // 完了/失敗になったら停止
      if (gen?.status === "completed" || gen?.status === "failed") return;
      fetchOnce();
    }, 2500);
    return () => {
      active = false;
      window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, gen?.status]);

  const handleDownload = async () => {
    if (!gen?.output_image_url) return;
    track("image_download", { generation_id: gen.id });
    try {
      const res = await fetch(gen.output_image_url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kagem-${gen.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("ダウンロードに失敗しました");
    }
  };

  const handleAnimate = () => {
    if (!gen) return;
    track("image_to_video_click", { generation_id: gen.id });
    navigate({
      to: "/create/video",
      search: { mode: "i2v", fromImage: gen.id } as never,
    });
  };

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  if (error || !gen) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <h1 className="text-xl font-bold">読み込みに失敗しました</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {error ?? "画像データが見つかりませんでした。"}
            </p>
            <Button asChild className="mt-6">
              <Link to="/create/image">もう一度作る</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  // 生成中
  if (gen.status !== "completed" && gen.status !== "failed") {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h1 className="mt-4 text-xl font-bold">画像を生成中…</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              通常 10〜30 秒ほどで完成します。このまま少々お待ちください。
            </p>
          </Card>
        </div>
      </main>
    );
  }

  // 失敗
  if (gen.status === "failed") {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <h1 className="text-xl font-bold">生成に失敗しました</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {gen.error_message ?? "もう一度お試しください。クレジットは返却されています。"}
            </p>
            <Button asChild className="mt-6">
              <Link to="/create/image">もう一度作る</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-6 sm:py-8">
      <div className="mx-auto max-w-xl">
        <header className="mb-4 flex items-center gap-3">
          <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Wand2 className="h-4 w-4" />
          </div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">完成しました</h1>
        </header>


        <Card className="overflow-hidden bg-muted/30 p-2">
          {gen.output_image_url ? (
            <div className="flex items-center justify-center">
              <img
                src={gen.output_image_url}
                alt={gen.prompt_original ?? "generated image"}
                className="mx-auto block h-auto max-h-[60vh] w-auto max-w-full rounded-md object-contain"
                onError={async (e) => {
                  // 署名URLの期限切れ対策：パスから再署名
                  const img = e.currentTarget;
                  if (img.dataset.retried === "1") return;
                  img.dataset.retried = "1";
                  const m = gen.output_image_url?.match(/\/object\/sign\/([^/]+)\/([^?]+)/);
                  if (!m) return;
                  const [, bucket, path] = m;
                  const { data } = await supabase.storage
                    .from(bucket)
                    .createSignedUrl(decodeURIComponent(path), 60 * 60);
                  if (data?.signedUrl) img.src = data.signedUrl;
                }}
              />
            </div>

          ) : (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
              画像URLがまだ反映されていません
            </div>
          )}
        </Card>


        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button onClick={handleDownload} variant="outline" size="lg" className="h-12">
            <Download className="mr-2 h-4 w-4" />
            ダウンロード
          </Button>
          <Button
            onClick={handleAnimate}
            size="lg"
            className="h-12 bg-gradient-to-r from-primary to-primary/80"
          >
            <Video className="mr-2 h-4 w-4" />
            この画像を動画化
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/create/image">
              <RefreshCw className="mr-2 h-4 w-4" />
              もう1枚作る
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate({
                to: "/create/image",
                search: { from: gen.id } as never,
              })
            }
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            設定を変えて再生成
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/history">履歴を見る</Link>
          </Button>
        </div>

        {/* メタ情報 */}
        <Card className="mt-6 p-5">
          <h2 className="text-sm font-semibold">生成情報</h2>
          <dl className="mt-3 space-y-2 text-xs">
            {gen.prompt_original && (
              <Row label="プロンプト" value={gen.prompt_original} />
            )}
            {gen.prompt_translated && (
              <Row label="英訳" value={gen.prompt_translated} mono />
            )}
            <Row label="スタイル" value={gen.style_preset ?? "なし"} />
            <Row label="比率" value={gen.aspect_ratio ?? "—"} />
            <Row label="品質" value={gen.quality_tier ?? "—"} />
            <Row label="消費クレジット" value={`${gen.credits_consumed}`} />
          </dl>
        </Card>
      </div>
    </main>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
      <dt className="w-32 shrink-0 text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-foreground" : "text-foreground"}>{value}</dd>
    </div>
  );
}
